import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import GermanDateInput from "@/components/GermanDateInput";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Trash2, UserCircle2, Plus, Tag, Users, CheckSquare, MessageSquare,
  X, Send, Clock, Copy, Link as LinkIcon,
} from "lucide-react";

const LABEL_COLORS = {
  green: "#61BD4F", yellow: "#F2D600", orange: "#FF9F1A",
  red: "#EB5A46", purple: "#C377E0", blue: "#0079BF",
};

const initials = (name) => (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const renderMentions = (text) => {
  const parts = (text || "").split(/(@[A-Za-zÄÖÜäöüß\-]+(?:\s[A-Za-zÄÖÜäöüß\-]+)?)/g);
  return parts.map((p, i) => (p.startsWith("@") ? <span key={i} className="text-[#00807E] font-semibold">{p}</span> : <span key={i}>{p}</span>));
};

export default function BoardCardModal({ open, onOpenChange, cardId, teamMembers, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [descDirty, setDescDirty] = useState(false);  const [newComment, setNewComment] = useState("");
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const commentInputRef = React.useRef(null);
  const [addingChecklist, setAddingChecklist] = useState(false);
  const [newChecklistTitle, setNewChecklistTitle] = useState("");
  const [itemDrafts, setItemDrafts] = useState({});
  const [labelName, setLabelName] = useState("");
  const [labelColor, setLabelColor] = useState("blue");
  const [clientOptions, setClientOptions] = useState([]);

  const teamById = Object.fromEntries((teamMembers || []).map((m) => [m.user_id, m]));

  const load = async () => {
    if (!cardId) return;
    setLoading(true);
    try {
      const r = await api.get(`/boards/cards/${cardId}/detail`);
      setDetail(r.data);
      setTitle(r.data.title || "");
      setDescription(r.data.description || "");
      setDescDirty(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open && cardId) load(); }, [open, cardId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && detail?.board_id === "avgs" && clientOptions.length === 0) {
      api.get("/clients").then((r) => setClientOptions(r.data)).catch(() => {});
    }
  }, [open, detail?.board_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => { await load(); onChanged?.(); };

  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

  const saveTitle = async () => {
    if (!title.trim() || title === detail?.title) return;
    try { await api.patch(`/boards/cards/${cardId}`, { title: title.trim() }); refresh(); } catch (e) { err(e); }
  };

  const saveDescription = async () => {
    try { await api.patch(`/boards/cards/${cardId}`, { description }); setDescDirty(false); refresh(); } catch (e) { err(e); }
  };

  const setDueDate = async (val) => {
    try { await api.patch(`/boards/cards/${cardId}`, { due_date: val || null }); refresh(); } catch (e) { err(e); }
  };

  const setClientLink = async (clientId) => {
    try { await api.patch(`/boards/cards/${cardId}`, { client_id: clientId || null }); refresh(); } catch (e) { err(e); }
  };

  const toggleAssignee = async (uid) => {
    const cur = detail.assignee_ids || [];
    const next = cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid];
    try { await api.patch(`/boards/cards/${cardId}`, { assignee_ids: next }); refresh(); } catch (e) { err(e); }
  };

  const addLabel = async () => {
    if (!labelName.trim()) return;
    try {
      await api.post(`/boards/cards/${cardId}/labels`, { name: labelName.trim(), color: labelColor });
      setLabelName("");
      refresh();
    } catch (e) { err(e); }
  };

  const removeLabel = async (labelId) => {
    try { await api.delete(`/boards/cards/${cardId}/labels/${labelId}`); refresh(); } catch (e) { err(e); }
  };

  const addChecklist = async () => {
    if (!newChecklistTitle.trim()) { setAddingChecklist(false); return; }
    try {
      await api.post(`/boards/cards/${cardId}/checklists`, { title: newChecklistTitle.trim() });
      setNewChecklistTitle("");
      setAddingChecklist(false);
      refresh();
    } catch (e) { err(e); }
  };

  const deleteChecklist = async (checklistId) => {
    if (!window.confirm("Checkliste wirklich löschen?")) return;
    try { await api.delete(`/boards/checklists/${checklistId}`); refresh(); } catch (e) { err(e); }
  };

  const addItem = async (checklistId) => {
    const draft = itemDrafts[checklistId] || {};
    if (!draft.text?.trim()) return;
    try {
      await api.post(`/boards/checklists/${checklistId}/items`, { text: draft.text.trim(), assignee_id: draft.assignee_id || null });
      setItemDrafts((d) => ({ ...d, [checklistId]: { text: "", assignee_id: "" } }));
      refresh();
    } catch (e) { err(e); }
  };

  const toggleItem = async (item) => {
    try { await api.patch(`/boards/checklist-items/${item.item_id}`, { done: !item.done }); refresh(); } catch (e) { err(e); }
  };

  const setItemAssignee = async (item, uid) => {
    try { await api.patch(`/boards/checklist-items/${item.item_id}`, { assignee_id: uid || null }); refresh(); } catch (e) { err(e); }
  };

  const deleteItem = async (itemId) => {
    try { await api.delete(`/boards/checklist-items/${itemId}`); refresh(); } catch (e) { err(e); }
  };

  const onCommentChange = (val) => {
    setNewComment(val);
    const match = val.match(/@([A-Za-zÄÖÜäöüß\-]*)$/);
    if (match) {
      const q = match[1].toLowerCase();
      setMentionSuggestions((teamMembers || []).filter((m) => (m.name || "").toLowerCase().includes(q)).slice(0, 6));
    } else {
      setMentionSuggestions([]);
    }
  };

  const pickMention = (name) => {
    setNewComment((val) => {
      const next = val.replace(/@([A-Za-zÄÖÜäöüß\-]*)$/, `@${name} `);
      requestAnimationFrame(() => {
        const el = commentInputRef.current;
        if (el) { el.focus(); el.setSelectionRange(next.length, next.length); }
      });
      return next;
    });
    setMentionSuggestions([]);
  };

  const sendComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/boards/cards/${cardId}/comments`, { text: newComment.trim() });
      setNewComment("");
      setMentionSuggestions([]);
      refresh();
    } catch (e) { err(e); }
  };

  const deleteComment = async (feedId) => {
    try { await api.delete(`/boards/comments/${feedId}`); refresh(); } catch (e) { err(e); }
  };

  const remove = async () => {
    if (!window.confirm("Karte wirklich löschen?")) return;
    try {
      await api.delete(`/boards/cards/${cardId}`);
      toast.success("Karte gelöscht");
      onChanged?.();
      onOpenChange(false);
    } catch (e) { err(e); }
  };

  const duplicate = async () => {
    try {
      await api.post(`/boards/cards/${cardId}/duplicate`);
      toast.success("Karte dupliziert (inkl. Checklisten)");
      onChanged?.();
      onOpenChange(false);
    } catch (e) { err(e); }
  };

  if (!cardId) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto bg-white" data-testid="board-card-modal">
        {loading || !detail ? (
          <>
            <DialogHeader><DialogTitle className="sr-only">Karte wird geladen</DialogTitle></DialogHeader>
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500 py-10 text-center">Lädt Karte…</div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                <input
                  data-testid="board-card-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={saveTitle}
                  className="w-full text-lg font-heading font-light tracking-tight border-b border-transparent hover:border-slate-200 focus:border-slate-400 focus:outline-none py-1"
                />
              </DialogTitle>
              <DialogDescription className="sr-only">Karten-Details, Mitglieder, Labels, Checklisten und Aktivität</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 mt-2">
              {/* ---------- Left: details ---------- */}
              <div className="space-y-6 min-w-0">
                {/* Members + Labels row */}
                <div className="flex flex-wrap gap-8">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2 flex items-center gap-1.5"><Users size={11} /> Mitglieder</div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(detail.assignee_ids || []).map((uid) => (
                        <span key={uid} title={teamById[uid]?.name || uid}
                          className="w-8 h-8 rounded-full bg-slate-800 text-white text-[11px] font-bold flex items-center justify-center">
                          {initials(teamById[uid]?.name)}
                        </span>
                      ))}
                      <Popover>
                        <PopoverTrigger asChild>
                          <button data-testid="board-card-add-member-btn" className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600">
                            <Plus size={14} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="bg-white w-64" data-testid="board-card-member-popover">
                          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">Mitglieder</div>
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {(teamMembers || []).map((m) => {
                              const active = (detail.assignee_ids || []).includes(m.user_id);
                              return (
                                <button key={m.user_id} onClick={() => toggleAssignee(m.user_id)}
                                  data-testid={`board-card-assignee-${m.user_id}`}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-slate-50 ${active ? "bg-slate-50" : ""}`}>
                                  <span className="w-5 h-5 rounded-full bg-slate-800 text-white text-[9px] font-bold flex items-center justify-center">{initials(m.name)}</span>
                                  <span className="flex-1 truncate">{m.name}</span>
                                  {active && <span className="text-[#00807E] text-xs">✓</span>}
                                </button>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {detail.board_id === "avgs" && (
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2 flex items-center gap-1.5"><LinkIcon size={11} /> Kunde verknüpfen</div>
                      <select
                        value={detail.client_id || ""}
                        onChange={(e) => setClientLink(e.target.value)}
                        className="input-tech text-xs w-56"
                        data-testid="board-card-client-link-select"
                      >
                        <option value="">— kein Kunde —</option>
                        {clientOptions.map((c) => (
                          <option key={c.client_id} value={c.client_id}>{c.name}</option>
                        ))}
                      </select>
                      {detail.client_id && (
                        <div className="text-[10px] text-slate-500 mt-1.5">
                          Beim Verschieben nach „Vertrag erstellen“ wird für diesen Kunden automatisch der Vertrags-Entwurf erzeugt.
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2 flex items-center gap-1.5"><Tag size={11} /> Labels</div>
                    <div className="flex items-center gap-1.5 flex-wrap max-w-xs">
                      {(detail.labels || []).map((l) => (
                        <span key={l.id} data-testid={`board-card-label-${l.id}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-white"
                          style={{ backgroundColor: LABEL_COLORS[l.color] || "#0079BF" }}>
                          {l.name}
                          <button onClick={() => removeLabel(l.id)} data-testid={`board-card-remove-label-${l.id}`}><X size={11} /></button>
                        </span>
                      ))}
                      <Popover>
                        <PopoverTrigger asChild>
                          <button data-testid="board-card-add-label-btn" className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600">
                            <Plus size={14} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="bg-white w-64" data-testid="board-card-label-popover">
                          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">Neues Label</div>
                          <input value={labelName} onChange={(e) => setLabelName(e.target.value)} placeholder="Name (z. B. Bearbeitungsphase)"
                            className="w-full border border-slate-200 p-2 text-xs mb-2 focus:outline-none focus:border-slate-400"
                            data-testid="board-card-label-name-input" />
                          <div className="flex gap-1.5 mb-3">
                            {Object.entries(LABEL_COLORS).map(([key, hex]) => (
                              <button key={key} onClick={() => setLabelColor(key)} data-testid={`board-card-label-color-${key}`}
                                className={`w-7 h-7 ${labelColor === key ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
                                style={{ backgroundColor: hex }} />
                            ))}
                          </div>
                          <button onClick={addLabel} data-testid="board-card-label-create-btn"
                            className="w-full bg-slate-900 text-white text-xs font-medium py-2">Erstellen</button>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                {/* Due date */}
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2 flex items-center gap-1.5"><Clock size={11} /> Fälligkeitsdatum</div>
                  <GermanDateInput data-testid="board-card-due-date-input" value={detail.due_date || ""} onChange={(e) => setDueDate(e.target.value)} />
                  {detail.due_date && (
                    <button onClick={() => setDueDate("")} data-testid="board-card-clear-due-date" className="ml-3 text-xs text-slate-400 hover:text-slate-700 underline">entfernen</button>
                  )}
                </div>

                {/* Description */}
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">Beschreibung</div>
                  <textarea
                    data-testid="board-card-description-input"
                    value={description}
                    onChange={(e) => { setDescription(e.target.value); setDescDirty(true); }}
                    rows={3}
                    className="w-full border border-slate-200 p-3 text-sm focus:outline-none focus:border-slate-400"
                    placeholder="Details zur Aufgabe…"
                  />
                  {descDirty && (
                    <button onClick={saveDescription} data-testid="board-card-save-description-btn" className="mt-2 bg-slate-900 text-white text-xs font-medium px-3 py-1.5">Speichern</button>
                  )}
                </div>

                {/* Checklists */}
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5"><CheckSquare size={11} /> Checklisten</div>
                    {!addingChecklist && (
                      <button onClick={() => setAddingChecklist(true)} data-testid="board-card-add-checklist-btn" className="text-xs font-medium text-[#00807E] hover:underline flex items-center gap-1">
                        <Plus size={12} /> Checkliste hinzufügen
                      </button>
                    )}
                  </div>
                  {addingChecklist && (
                    <div className="flex gap-2">
                      <input autoFocus value={newChecklistTitle} onChange={(e) => setNewChecklistTitle(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addChecklist()}
                        placeholder="Titel der Checkliste" className="flex-1 border border-slate-300 p-2 text-sm focus:outline-none focus:border-slate-500"
                        data-testid="board-card-new-checklist-title" />
                      <button onClick={addChecklist} data-testid="board-card-new-checklist-save" className="bg-slate-900 text-white text-xs font-medium px-3">Hinzufügen</button>
                      <button onClick={() => { setAddingChecklist(false); setNewChecklistTitle(""); }} className="text-xs text-slate-500 px-2">Abbrechen</button>
                    </div>
                  )}

                  {(detail.checklists || []).map((cl) => {
                    const total = cl.items.length;
                    const done = cl.items.filter((i) => i.done).length;
                    const pct = total ? Math.round((done / total) * 100) : 0;
                    return (
                      <div key={cl.checklist_id} data-testid={`board-checklist-${cl.checklist_id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-semibold text-slate-800">{cl.title}</div>
                          <button onClick={() => deleteChecklist(cl.checklist_id)} data-testid={`board-checklist-delete-${cl.checklist_id}`} className="text-slate-400 hover:text-red-600">
                            <Trash2 size={13} strokeWidth={1.5} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[10px] font-mono text-slate-500 w-9">{pct}%</span>
                          <div className="flex-1 h-1.5 bg-slate-100"><div className="h-1.5 bg-emerald-500" style={{ width: `${pct}%` }} /></div>
                        </div>
                        <ul className="space-y-1.5">
                          {cl.items.map((it) => (
                            <li key={it.item_id} className="flex items-center gap-2 group" data-testid={`board-checklist-item-${it.item_id}`}>
                              <input type="checkbox" checked={!!it.done} onChange={() => toggleItem(it)} data-testid={`board-checklist-item-checkbox-${it.item_id}`} />
                              <span className={`flex-1 text-sm ${it.done ? "line-through text-slate-400" : "text-slate-800"}`}>{it.text}</span>
                              <select value={it.assignee_id || ""} onChange={(e) => setItemAssignee(it, e.target.value)}
                                data-testid={`board-checklist-item-assignee-${it.item_id}`}
                                className="text-[10px] border border-slate-200 px-1 py-0.5 max-w-[110px] opacity-70 group-hover:opacity-100">
                                <option value="">Zuweisen…</option>
                                {(teamMembers || []).map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                              </select>
                              <button onClick={() => deleteItem(it.item_id)} data-testid={`board-checklist-item-delete-${it.item_id}`} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600">
                                <X size={13} />
                              </button>
                            </li>
                          ))}
                        </ul>
                        <div className="flex gap-2 mt-2">
                          <input value={itemDrafts[cl.checklist_id]?.text || ""}
                            onChange={(e) => setItemDrafts((d) => ({ ...d, [cl.checklist_id]: { ...d[cl.checklist_id], text: e.target.value } }))}
                            onKeyDown={(e) => e.key === "Enter" && addItem(cl.checklist_id)}
                            placeholder="Element hinzufügen"
                            className="flex-1 border border-slate-200 p-1.5 text-xs focus:outline-none focus:border-slate-400"
                            data-testid={`board-checklist-new-item-${cl.checklist_id}`} />
                          <button onClick={() => addItem(cl.checklist_id)} data-testid={`board-checklist-add-item-btn-${cl.checklist_id}`}
                            className="text-xs font-medium bg-slate-100 hover:bg-slate-200 px-2">+</button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
                  <button onClick={duplicate} data-testid="board-card-duplicate-button" className="flex items-center gap-2 text-xs font-medium text-slate-600 hover:text-slate-900">
                    <Copy size={14} strokeWidth={1.5} /> Karte duplizieren
                  </button>
                  <button onClick={remove} data-testid="board-card-delete-button" className="flex items-center gap-2 text-xs font-medium text-red-600 hover:text-red-800">
                    <Trash2 size={14} strokeWidth={1.5} /> Karte löschen
                  </button>
                </div>
              </div>

              {/* ---------- Right: comments & activity ---------- */}
              <div className="border-t lg:border-t-0 lg:border-l border-slate-200 pt-6 lg:pt-0 lg:pl-6">
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-3 flex items-center gap-1.5"><MessageSquare size={11} /> Kommentare und Aktivität</div>
                <div className="flex gap-2 mb-4 relative">
                  <input value={newComment} onChange={(e) => onCommentChange(e.target.value)}
                    ref={commentInputRef}
                    onKeyDown={(e) => e.key === "Enter" && !mentionSuggestions.length && sendComment()}
                    placeholder="Kommentar schreiben… (@ für Erwähnung)" data-testid="board-card-comment-input"
                    className="flex-1 border border-slate-200 p-2 text-xs focus:outline-none focus:border-slate-400" />
                  <button onClick={sendComment} data-testid="board-card-comment-send-btn" className="bg-slate-900 text-white px-2.5"><Send size={13} /></button>
                  {mentionSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-slate-200 shadow-lg z-10" data-testid="board-card-mention-suggestions">
                      {mentionSuggestions.map((m) => (
                        <button key={m.user_id} onClick={() => pickMention(m.name)} data-testid={`board-card-mention-option-${m.user_id}`}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-slate-800 text-white text-[8px] font-bold flex items-center justify-center">{initials(m.name)}</span>
                          {m.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-3 max-h-[420px] overflow-y-auto">
                  {(detail.feed || []).map((f) => (
                    <div key={f.feed_id} className="text-xs" data-testid={`board-card-feed-${f.feed_id}`}>
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <span className="w-5 h-5 rounded-full bg-slate-800 text-white text-[8px] font-bold flex items-center justify-center shrink-0">{initials(f.user_name)}</span>
                        <span className="font-medium text-slate-700">{f.user_name}</span>
                        <span className="text-[10px]">{fmtDateTime(f.created_at)}</span>
                      </div>
                      <div className={`mt-1 ml-7 ${f.kind === "comment" ? "bg-slate-50 p-2" : "text-slate-500 italic"}`}>
                        {renderMentions(f.text)}
                        {f.kind === "comment" && (
                          <button onClick={() => deleteComment(f.feed_id)} data-testid={`board-card-comment-delete-${f.feed_id}`} className="ml-2 text-slate-300 hover:text-red-600"><X size={11} className="inline" /></button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(detail.feed || []).length === 0 && (
                    <div className="text-xs text-slate-400 flex items-center gap-2"><UserCircle2 size={14} /> Noch keine Aktivität</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
